package com.kinoshazam.screens.main

import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.kinoshazam.uiKit.theme.UiKitTheme
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.decide_banner_title
import kinoshazam.resources.generated.resources.dice
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import kotlin.math.PI
import kotlin.math.sin

@Composable
fun DecideToolbarIcon(
    visible: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    if (!visible) return
    val interaction = remember { MutableInteractionSource() }
    val pressed by interaction.collectIsPressedAsState()
    DecideDiceIcon(
        tint = UiKitTheme.colors.color_1,
        size = 24.dp,
        pressed = pressed,
        modifier = modifier
            .padding(end = 8.dp)
            .clickable(
                interactionSource = interaction,
                indication = null,
                onClick = onClick,
            ),
        contentDescription = stringResource(Res.string.decide_banner_title),
    )
}

@Composable
fun DecideCircleButton(
    visible: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    size: Dp = 44.dp,
) {
    if (!visible) return
    val interaction = remember { MutableInteractionSource() }
    val hovered by interaction.collectIsHoveredAsState()
    val pressed by interaction.collectIsPressedAsState()
    val fill = hovered || pressed

    Box(
        modifier = modifier
            .size(size)
            .clip(CircleShape)
            .background(if (fill) UiKitTheme.colors.color_1 else Color.Transparent)
            .border(1.5.dp, UiKitTheme.colors.color_1, CircleShape)
            .clickable(
                interactionSource = interaction,
                indication = null,
                onClick = onClick,
            ),
        contentAlignment = Alignment.Center,
    ) {
        DecideDiceIcon(
            tint = if (fill) UiKitTheme.colors.color_7 else UiKitTheme.colors.color_2,
            size = size * 0.48f,
            pressed = pressed,
            contentDescription = stringResource(Res.string.decide_banner_title),
        )
    }
}

@Composable
private fun DecideDiceIcon(
    tint: Color,
    size: Dp,
    pressed: Boolean,
    modifier: Modifier = Modifier,
    contentDescription: String,
) {
    val loop = rememberInfiniteTransition(label = "decide_dice")
    val cycle by loop.animateFloat(
        initialValue = 0f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 3800, easing = LinearEasing),
            repeatMode = RepeatMode.Restart,
        ),
        label = "dice_cycle",
    )
    val (rotation, scale) = diceRoll(cycle, pressed)

    Icon(
        painter = painterResource(Res.drawable.dice),
        contentDescription = contentDescription,
        tint = tint,
        modifier = modifier
            .size(size)
            .graphicsLayer {
                rotationZ = rotation
                scaleX = scale
                scaleY = scale
            },
    )
}

private fun diceRoll(cycle: Float, pressed: Boolean): Pair<Float, Float> {
    val idle = sin(cycle * 22f) * 7f
    val rotation = when {
        pressed -> 18f
        cycle < 0.68f -> idle
        cycle < 0.92f -> {
            val t = FastOutSlowInEasing.transform((cycle - 0.68f) / 0.24f)
            t * 360f
        }
        else -> idle * 0.3f
    }
    val scale = when {
        pressed -> 0.88f
        cycle in 0.68f..0.92f -> {
            val t = (cycle - 0.68f) / 0.24f
            1f + 0.12f * sin(t * PI.toFloat())
        }
        else -> 1f
    }
    return rotation to scale
}
